import { useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import type { AppBreadcrumbItem } from './AppBreadcrumb';

/**
 * Module-level external store (not React Context) for the current detail
 * page's entity title, read by TopBar's breadcrumb.
 *
 * Why not Context: every page wraps ITSELF in <AppLayout>...</AppLayout> —
 * the page component is the PARENT that creates the AppLayout element, so
 * the page's own hooks run at a tree position *above* AppLayout's Provider,
 * not inside it. A Context set up inside AppLayout can never be read by the
 * page that renders it. A plain external store has no such tree-position
 * requirement — any component can set it, any component can subscribe.
 */
let currentTitle: string | null = null;
const listeners = new Set<() => void>();

function setEntityTitle(title: string | null) {
  currentTitle = title;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return currentTitle;
}

/** TopBar-only: the current detail page's entity title, or null outside detail pages. */
export function useBreadcrumbEntityTitle() {
  return useSyncExternalStore(subscribe, getSnapshot, () => null);
}

/**
 * Detail pages (scenario/persona/learner/...) call this with the loaded
 * entity's display name so TopBar's breadcrumb shows it as the final crumb
 * (e.g. "Admin > Scenarios > Client Objection Handling"). There is
 * intentionally no in-page breadcrumb — TopBar is the single source.
 */
export function usePageBreadcrumbTitle(title: string | null | undefined) {
  const resolved = title ?? null;
  useEffect(() => {
    setEntityTitle(resolved);
    return () => setEntityTitle(null);
  }, [resolved]);
}

export interface ManagementBreadcrumbContext {
  locationKey: string;
  items: AppBreadcrumbItem[];
  backHref: string;
}

let management: { owner: symbol; context: ManagementBreadcrumbContext } | null = null;
const managementListeners = new Set<() => void>();
const notifyManagement = () => managementListeners.forEach((listener) => listener());
const managementSnapshot = () => management;
const subscribeManagement = (listener: () => void) => {
  managementListeners.add(listener);
  return () => managementListeners.delete(listener);
};

function normalizeLocation(key: string) {
  const url = new URL(key, 'https://navigation.local');
  url.searchParams.sort();
  return url.pathname + url.search;
}

/** One owning route publishes verified context; outgoing cleanup cannot clear its successor. */
export function usePageManagementContext(context: ManagementBreadcrumbContext | null) {
  const owner = useRef(Symbol('management-route'));
  const locationKey = context?.locationKey;
  const backHref = context?.backHref;
  const serializedItems = JSON.stringify(context?.items ?? []);
  // Layout phase: cleanup and republish land before paint, so no breadcrumb flicker.
  useLayoutEffect(() => {
    if (!locationKey || !backHref) return;
    const token = owner.current;
    management = {
      owner: token,
      context: {
        locationKey: normalizeLocation(locationKey),
        backHref,
        items: JSON.parse(serializedItems) as AppBreadcrumbItem[],
      },
    };
    notifyManagement();
    return () => {
      if (management?.owner !== token) return;
      management = null;
      notifyManagement();
    };
  }, [locationKey, backHref, serializedItems]);
}

export function useManagementBreadcrumb(locationKey: string) {
  const published = useSyncExternalStore(subscribeManagement, managementSnapshot, () => null);
  return published?.context.locationKey === normalizeLocation(locationKey)
    ? published.context
    : null;
}
